import React from "react";
import styled from "styled-components";
import { Button, Col, Modal, Row } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useFieldArray, useForm } from "react-hook-form";
import { queryCache, useMutation } from "react-query";
import { joiResolver } from "@hookform/resolvers";
import Joi from "@hapi/joi";
import { TInput } from "../form/Field/TInput";
import { apiAxios } from "services/HttpServices";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { CustomLinkButton } from "../job/add/form/HotStampingRadio";

const createPaper = async (paper) => {
  try {
    const res = await apiAxios.post(`/jobs/paper_materials`, paper);
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const paperSchema = Joi.object({
  name: Joi.string().required(),
  grammage: Joi.array().items(Joi.object().keys({ value: Joi.string() })),
});

function AddPaperModal(props) {
  const [showAddGrammage, setShowAddGrammage] = React.useState(false);
  const {
    visible,
    setPaperModalVisible,
    setValue,
    addPaperGrammageFrom,
  } = props;

  const [error, setError] = React.useState(null);
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);
  const { control, handleSubmit, reset, getValues, errors } = useForm({
    resolver: joiResolver(paperSchema),
    defaultValues: {
      name: null,
      grammage: [{ value: null }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "grammage",
  });

  const onSubmit = async (data) => {
    setError("");
    setLoading("createPaper");
    let value = [];

    if (data.grammage?.length > 0) {
      data.grammage.map((data) => value.push(parseInt(data.value)));
      data.grammage = value;
    }
    try {
      await mutatePaperMaterials(data);
      finishLoading("createPaper");
      setShowAddGrammage(false);
      reset();
    } catch (error) {
      finishLoading("createPaper");
      reset();
    }
  };

  //Mutation
  const [mutatePaperMaterials] = useMutation(createPaper, {
    onSuccess: (newPaper) => {
      queryCache.setQueryData(["paper-materials"], function (oldData) {
        return [
          ...oldData,
          {
            ...newPaper,
          },
        ];
      });
      setValue(`${addPaperGrammageFrom}.paper_material_id`, newPaper.id);
      reset();
      setPaperModalVisible(false);
    },
    onError: (e) => {
      setError(e);
    },
  });

  const handleShowAddGrammage = () => {
    if (showAddGrammage) {
      setShowAddGrammage(false);
      reset({
        ...getValues(),
        grammage: [{ value: null }],
      });
    } else {
      setShowAddGrammage(true);
    }
  };

  const handleCloseModal = () => {
    setShowAddGrammage(false);
    setPaperModalVisible(false);
    reset();
  };

  return (
    <Modal
      title="Add new paper"
      visible={visible}
      onCancel={handleCloseModal}
      onOk={handleSubmit(onSubmit)}
      confirmLoading={notificationState.loading.createPaper}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <TInput
          errors={errors}
          label="Paper Name*"
          name="name"
          placeholder="Name"
          autoFocus={true}
          propsInput={{
            control: control,
          }}
        />
        {showAddGrammage && (
          <StyledAppend>
            {fields.map((item, index) => {
              return (
                <Row key={item.id} gutter={6}>
                  <Col xs={22}>
                    <TInput
                      errors={errors}
                      name={`grammage[${index}].value`}
                      placeholder="Grammage"
                      defaultValue={item.value}
                      autoFocus={true}
                      propsInput={{
                        control: control,
                      }}
                    />
                  </Col>
                  <Col xs={2}>
                    <Button
                      type="dashed"
                      danger
                      icon={<DeleteOutlined />}
                      size="medium"
                      onClick={() => remove(index)}
                      disabled={index === 0}
                    />
                  </Col>
                </Row>
              );
            })}
            <CustomLinkButton
              onClick={() => {
                append({ value: null });
              }}
              className="button"
              icon={<PlusOutlined />}
            >
              Grammage
            </CustomLinkButton>
          </StyledAppend>
        )}
        {showAddGrammage ? (
          <Button onClick={handleShowAddGrammage} type="outlined" danger>
            Remove grammage
          </Button>
        ) : (
          <CustomLinkButton
            onClick={handleShowAddGrammage}
            className="button"
            icon={<PlusOutlined />}
          >
            Add Paper GSM
          </CustomLinkButton>
        )}
      </form>
    </Modal>
  );
}

const StyledAppend = styled.div`
  padding: ${(props) => props.theme.spacing[3]};
  background-color: ${(props) => props.theme.colors.grey[100]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin-bottom: ${(props) => props.theme.spacing[3]};
`;

export default AddPaperModal;
